import { setupFields, setFieldError } from './helpers/fieldFactory';
import { validateAllFields, attachLiveRevalidation, applyServerErrors } from './helpers/validation';
import { showSkeletonLoading, hideSkeletonLoading } from './helpers/loading';
import { buildSubmitPayload } from './helpers/buildSubmitPayload';
import { showToast, ToastType } from '../../toast';
import { SERVICE_FLOW_QUERY_KEYS } from '../constants/queryKeys';

export class ServiceFlowConfigurator {
  constructor(
    element,
    callbackAfterConfiguratorValidation,
    translations,
    warrantyApiConfig,
    modelNo,
  ) {
    this._refs = {
      el: element,
    };
    this.configuratorId = '';
    this.serialNumber = '';
    this.modelNo = modelNo || '';
    this.fields = [];
    this.translations = translations || {};
    // Needed by fields that call fetchProductData (e.g. Accessories field).
    this.warrantyApiConfig = warrantyApiConfig || {};
    this.isAllFieldValidationsValid = false;
    this.isSubmitted = false;
    this.isReady = false;
    this.callbackAfterConfiguratorValidation = callbackAfterConfiguratorValidation;

    // Show the loading skeleton immediately, before data is fetched.
    showSkeletonLoading(this._refs.el);
  }

  fetchConfiguratorData(serialNumber, name) {
    this.serialNumber = serialNumber;
    return this.getConfiguratorData(name);
  }

  async renderConfigurator(apiData) {
    this.isReady = false;
    hideSkeletonLoading(this._refs.el);

    if (!apiData) {
      const message =
        this.translations?.configurationNotFoundErrorMessage ??
        'Configurator is not found, please contact customer care';

      showToast({
        message: message,
        type: ToastType.ERROR,
        autoDismiss: 0,
      });
      return;
    }

    this.configuratorId = apiData.Id;
    this.fields = [];

    await Promise.all(apiData.ConfiguratorSections.map((section) => this.setupSection(section)));
    this.isReady = true;
  }

  async getConfiguratorData(name) {
    try {
      const configurationGuid = new URLSearchParams(window.location.search).get(
        SERVICE_FLOW_QUERY_KEYS.CONFIGURATION_GUID,
      );

      const url = '/api/config/ite/' + name + '?reset=true';

      const response = await fetch(url, {
        method: 'GET',
        headers: { 'Content-type': 'application/json' },
      });

      if (!response.ok) {
        throw new Error('Configurator request failed with status ' + response.status);
      }

      const data = await response.json();
      if (!data || !data.ConfiguratorSections) {
        throw new Error('Configurator "' + name + '" was not found or returned no sections');
      }

      // No configurationGuid (new order): never show values remembered by the backend.
      if (!configurationGuid) {
        this.clearPrefilledValues(data);
      }

      return data;
    } catch (error) {
      console.error('Configurator API request failed:', error);
      return null;
    }
  }

  clearPrefilledValues(data) {
    // .NET DateTime.MinValue - what the backend sends for an empty date.
    const emptyDate = '0001-01-01T00:00:00';

    const resetters = {
      Text: () => null,
      Date: () => emptyDate,
      Checked: () => false,
      PatientData: () => null,
      FileName: () => null,
      SelectedItem: (value) => (Array.isArray(value) ? [] : null),
    };

    const clearField = (field) => {
      Object.keys(resetters).forEach((key) => {
        if (field[key] !== undefined) {
          field[key] = resetters[key](field[key]);
        }
      });
    };

    data.ConfiguratorSections.forEach((section) =>
      (section.ConfiguratorFields || []).forEach(clearField),
    );
  }

  async setupSection(section) {
    const sectionContainer = document.createElement('div');
    sectionContainer.classList.add('item-card-configurator__section');
    sectionContainer.classList.add('item-card-configurator__section--visible');
    sectionContainer.dataset.configuratorSerialNo = this.serialNumber;
    this._refs.el.appendChild(sectionContainer);

    if (section.DisplayName || section.HelpText) {
      const sectionHeader = document.createElement('div');
      sectionHeader.className = 'dtp-field-section__header';

      if (section.DisplayName) {
        const sectionTitle = document.createElement('div');
        sectionTitle.className = 'dtp-field-section__title';
        sectionTitle.textContent = section.DisplayName;
        sectionHeader.appendChild(sectionTitle);
      }

      if (section.HelpText) {
        const sectionHelpText = document.createElement('span');
        sectionHelpText.className = 'dtp-helptext';
        sectionHelpText.textContent = section.HelpText;
        sectionHeader.appendChild(sectionHelpText);
      }

      sectionContainer.appendChild(sectionHeader);
    }

    const fieldsContainer = document.createElement('div');
    fieldsContainer.className = 'dtp-field-section__fields';
    sectionContainer.appendChild(fieldsContainer);

    await setupFields(section.ConfiguratorFields, fieldsContainer, {
      fields: this.fields,
      translations: this.translations,
      serialNumber: this.serialNumber,
      modelNo: this.modelNo,
      warrantyApiConfig: this.warrantyApiConfig,
      dateFormat: this.warrantyApiConfig?.dateFormat,
      isSubmitted: () => this.isSubmitted,
    });

    // Remove the whole section if rendering left it with no fields (e.g.
    // Accessories field with an empty/failed API response).
    if (!fieldsContainer.children.length) {
      sectionContainer.remove();
    }

    // Re-validate a field on blur, but only after the first submit attempt.
    attachLiveRevalidation(
      () => this.fields,
      fieldsContainer,
      this.translations,
      () => this.isSubmitted,
    );
  }

  getValues(action, configurationMode = 'new') {
    return buildSubmitPayload({
      fields: this.fields,
      configuratorId: this.configuratorId,
      serialNumber: this.serialNumber,
      warrantyApiConfig: this.warrantyApiConfig,
      action,
      configurationMode,
    });
  }

  // Back to the pre-submit state: no live revalidation and no visible errors.
  resetValidation() {
    this.isSubmitted = false;
    this.isAllFieldValidationsValid = false;
    this.fields.forEach((field) => setFieldError(field, ''));
  }

  renderServerErrors(errors) {
    this.isSubmitted = true;
    return applyServerErrors(this.fields, errors);
  }

  // Validates all required fields, toggling their "mandatory" error state.
  validate() {
    if (!this.isReady) {
      return false;
    }

    this.isSubmitted = true;
    this.isAllFieldValidationsValid = validateAllFields(this.fields, this.translations);

    if (typeof this.callbackAfterConfiguratorValidation === 'function') {
      this.callbackAfterConfiguratorValidation();
    }

    return this.isAllFieldValidationsValid;
  }
}
